// =====================================================================
// Tiempo de pose de un servicio (E2): aplicación + pose + terminación.
// Durante la pose la persona queda libre (F6); el sillón/box no. Lógica
// pura: la misma regla que el check de la base (los tres suman la
// duración, la pose dura algo, ninguno negativo).
// =====================================================================

export type ServicePose = {
  applyMinutes: number;
  processingMinutes: number;
  finishMinutes: number;
};

/** Mensaje de error o null si la pose es válida para esa duración. */
export function poseError(duration: number, pose: ServicePose | null): string | null {
  if (!pose) return null;
  const { applyMinutes: a, processingMinutes: p, finishMinutes: f } = pose;
  if (![a, p, f].every((n) => Number.isInteger(n))) {
    return "Los tiempos tienen que ser minutos enteros.";
  }
  if (a < 0 || f < 0) return "Los tiempos no pueden ser negativos.";
  if (p <= 0) return "La pose tiene que durar al menos 1 minuto.";
  if (a + p + f !== duration) {
    return `Aplicación + pose + terminación tienen que sumar ${duration} min (suman ${a + p + f}).`;
  }
  return null;
}

/** Tramo de pose de un turno: desde `offset` min después del inicio, `minutes` de largo. */
export type AppointmentPose = { offset: number; minutes: number };

/**
 * Pose efectiva de un turno (F6): la que guardó la reserva online (con la
 * opción elegida) o, si no tiene, la de su servicio cuando la duración del
 * turno coincide con los tres tramos. Así también aplica a los turnos que
 * se cargan desde el panel y a los anteriores a F6.
 */
export function appointmentPose(
  appt: { duration: number; processingOffsetMinutes: number | null; processingMinutes: number | null },
  service: { applyMinutes: number | null; processingMinutes: number | null; finishMinutes: number | null } | null,
): AppointmentPose | null {
  if (appt.processingOffsetMinutes !== null && appt.processingMinutes !== null && appt.processingMinutes > 0) {
    if (appt.processingOffsetMinutes + appt.processingMinutes > appt.duration) return null;
    return { offset: appt.processingOffsetMinutes, minutes: appt.processingMinutes };
  }
  if (!service) return null;
  const { applyMinutes: a, processingMinutes: p, finishMinutes: f } = service;
  if (a === null || p === null || f === null || p <= 0) return null;
  if (a + p + f !== appt.duration) return null;
  return { offset: a, minutes: p };
}

/**
 * Tramos (en minutos desde medianoche) en que la persona está ocupada: con
 * pose, la aplicación y la terminación; la pose queda libre.
 */
export function busySpans(
  startMin: number,
  endMin: number,
  pose: AppointmentPose | null,
): Array<[number, number]> {
  if (!pose) return [[startMin, endMin]];
  const ps = startMin + pose.offset;
  const pe = ps + pose.minutes;
  return ([[startMin, ps], [pe, endMin]] as Array<[number, number]>).filter(([a, b]) => b > a);
}

/** ¿Se pisan los tramos ocupados de dos turnos? */
export function spansOverlap(a: Array<[number, number]>, b: Array<[number, number]>): boolean {
  return a.some(([as, ae]) => b.some(([bs, be]) => as < be && ae > bs));
}

/** Minutos en que la persona está ocupada (sin la pose). */
export function poseBusyMinutes(duration: number, pose: ServicePose | null): number {
  return pose ? pose.applyMinutes + pose.finishMinutes : duration;
}

/** "30 min", "1 h", "1 h 40 min". */
export function formatMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Duración de un servicio en la lista: "30 min" o rango de sus opciones "45 min–1 h". */
export function durationLabel(duration: number, variantDurations: number[] = []): string {
  if (variantDurations.length === 0) return formatMinutes(duration);
  const min = Math.min(...variantDurations);
  const max = Math.max(...variantDurations);
  return min === max ? formatMinutes(min) : `${formatMinutes(min)}–${formatMinutes(max)}`;
}

/**
 * Cambia la duración de un servicio con pose desde un formulario que no la
 * muestra: aplicación y terminación quedan, la pose absorbe la diferencia;
 * si no queda pose (≤ 0) se quita. Sin pose previa no toca nada.
 */
export function adjustPoseToDuration(
  apply: number | null,
  finish: number | null,
  duration: number,
):
  | Record<string, never>
  | { applyMinutes: number | null; processingMinutes: number | null; finishMinutes: number | null } {
  if (apply === null || finish === null) return {};
  const processing = duration - apply - finish;
  if (processing <= 0) {
    return { applyMinutes: null, processingMinutes: null, finishMinutes: null };
  }
  return { applyMinutes: apply, processingMinutes: processing, finishMinutes: finish };
}
